import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import { ApiResponse, PaginatedResponse } from '@/types';
import { readAuthToken } from '@/utils/authToken';

class ApiService {
  public client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.proviarcm.com/api',
      timeout: 30000, // 30 seconds - increased for file uploads that create folder structures
      withCredentials: true, // Include cookies in requests
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - add JWT token if available
    this.client.interceptors.request.use(
      (config) => {
        // FIXED: Removed noisy per-request console.log statements
        // Old code logged EVERY request/response which:
        // - Creates browser console noise in production
        // - Can log sensitive URLs (magic link tokens, OAuth codes)
        // - Logs auth tokens (even if redacted, still overhead)
        //
        // Gate verbose logging to development mode only via browser DevTools if needed

        config.headers = config.headers ?? {};
        const headers: any = config.headers;

        const setHeader = (name: string, value: string) => {
          if (typeof headers.set === 'function') {
            headers.set(name, value);
          } else {
            headers[name] = value;
          }
        };

        const deleteHeader = (name: string) => {
          if (typeof headers.delete === 'function') {
            headers.delete(name);
          } else {
            delete headers[name];
          }
        };

        // If sending FormData, do NOT force a JSON content-type.
        // Let the browser set the multipart boundary automatically.
        const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
        if (isFormData) {
          deleteHeader('Content-Type');
          deleteHeader('content-type');
        } else if (!(headers['Content-Type'] || headers['content-type'])) {
          setHeader('Content-Type', 'application/json');
        }

        const token = readAuthToken();

        if (token) {
          setHeader('Authorization', `Bearer ${token}`);
        }

        return config;
      },
      (error) => {
        // Only log actual errors (not every request)
        if (import.meta.env.DEV) {
          console.error('[API] Request interceptor error:', error);
        }
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        // FIXED: Don't log successful responses (too noisy)
        return response;
      },
      (error) => {
        // Log errors in development only
        if (import.meta.env.DEV) {
          console.error('[API] Response error:', {
            status: error.response?.status,
            url: error.config?.url,
            message: error.message,
          });
        }
        // Don't auto-redirect on 401 - let React Router handle it
        return Promise.reject(error);
      }
    );
  }

  // Generic methods
  async get<T>(url: string, config?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.client.get(url, config);
  }

  async post<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.client.post(url, data);
  }

  async put<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.client.put(url, data);
  }

  async patch<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.client.patch(url, data);
  }

  async delete<T>(url: string): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.client.delete(url);
  }

  // File upload
  async upload<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<AxiosResponse<ApiResponse<T>>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.client.post(url, formData, {
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          onProgress(Math.round(progress));
        }
      },
    });
  }

  // Paginated requests
  async getPaginated<T>(
    url: string,
    params?: any
  ): Promise<AxiosResponse<PaginatedResponse<T>>> {
    return this.client.get(url, { params });
  }
}

export const apiService = new ApiService();
export default apiService;
